/**
 * LoginEmailPage — Email/password sign-in form
 *
 * Reached via "Sign in with email" on the main LoginPage.
 * Same visual language: full-bleed bg image, form anchored bottom.
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts';
import { T, BRAND } from '@/styles/tokens';

function getAuthErrorMessage(err: any): string {
  const code = err.code;
  switch (code) {
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Invalid email or password';
    case 'auth/invalid-email':
      return 'Invalid email address';
    case 'auth/user-disabled':
      return 'This account has been disabled';
    case 'auth/too-many-requests':
      return 'Too many attempts. Try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection.';
    case 'auth/invalid-api-key':
      return 'Configuration error. Contact support.';
    default:
      if (err.message?.includes('API key')) {
        return 'Configuration error. Contact support.';
      }
      return 'Sign-in failed. Try again.';
  }
}

export function LoginEmailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, profile } = useAuth();

  const emailParam = new URLSearchParams(location.search).get('email') || '';
  const [email, setEmail] = useState(emailParam);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const params = new URLSearchParams(location.search);
  const next = params.get('next') || '/app/dashboard';
  const nextRef = useRef(next);

  const canSubmit = email && password && !isLoading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError('');
    setIsLoading(true);
    try {
      await signIn(email, password);
    } catch (err: any) {
      console.error('Login error:', err);
      setError(getAuthErrorMessage(err));
      setIsLoading(false);
    }
  };

  // Post-login redirect
  useEffect(() => {
    if (profile && isLoading) {
      setIsLoading(false);
      if (!profile.onboardingCompleted) {
        navigate(`/onboarding?next=${encodeURIComponent(nextRef.current)}`, { replace: true });
      } else {
        const postAuthAction = sessionStorage.getItem('postAuthAction');
        const destination = postAuthAction === 'save' ? '/app/result' : nextRef.current;
        if (postAuthAction) sessionStorage.removeItem('postAuthAction');
        navigate(destination, { replace: true });
      }
    }
  }, [profile, isLoading, navigate]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canSubmit) handleSubmit();
  };

  const inputBase: React.CSSProperties = {
    width: '100%',
    height: 52,
    borderRadius: 12,
    border: `1.5px solid ${T.border}`,
    backgroundColor: 'rgba(255,255,255,0.9)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    padding: '0 16px',
    fontFamily: BRAND.fontSans,
    fontSize: 14,
    color: BRAND.ink,
    outline: 'none',
    boxSizing: 'border-box' as const,
    transition: 'border-color 200ms ease',
  };

  return (
    <div
      style={{
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background image */}
      <img
        src="/brand/login-bg.png"
        alt=""
        role="presentation"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center top',
          zIndex: 0,
        }}
      />

      {/* Gradient overlay */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 1,
          background: `linear-gradient(to bottom, transparent 55%, rgba(250,248,243,0.5) 68%, rgba(250,248,243,0.92) 78%, ${BRAND.cream} 85%)`,
          pointerEvents: 'none',
        }}
      />

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Form area */}
      <div
        style={{
          position: 'relative',
          zIndex: 2,
          padding: '0 24px',
          paddingBottom: 'max(24px, env(safe-area-inset-bottom))',
          maxWidth: 420,
          width: '100%',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Back button */}
        <button
          onClick={() => navigate('/login')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            background: 'none',
            border: 'none',
            padding: '0 0 16px',
            fontFamily: BRAND.fontSans,
            fontSize: 14,
            fontWeight: 500,
            color: BRAND.ink,
            cursor: 'pointer',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m15 18-6-6 6-6" />
          </svg>
          Back
        </button>

        {/* Error */}
        {error && (
          <div
            style={{
              marginBottom: 12,
              padding: 10,
              backgroundColor: 'rgba(214,69,69,0.12)',
              borderRadius: 12,
              fontSize: 13,
              color: T.danger,
              textAlign: 'center',
              fontFamily: BRAND.fontSans,
            }}
          >
            {error}
          </div>
        )}

        {/* Email */}
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={(e) => { e.target.style.borderColor = BRAND.teal; }}
          onBlur={(e) => { e.target.style.borderColor = T.border; }}
          style={{ ...inputBase, marginBottom: 10 }}
          autoComplete="email"
          autoFocus
        />

        {/* Password */}
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={(e) => { e.target.style.borderColor = BRAND.teal; }}
          onBlur={(e) => { e.target.style.borderColor = T.border; }}
          style={{ ...inputBase, marginBottom: 10 }}
          autoComplete="current-password"
        />

        {/* Forgot password */}
        <div style={{ textAlign: 'right', marginBottom: 16 }}>
          <button
            onClick={() => navigate('/forgot-password')}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              fontFamily: BRAND.fontSans,
              fontSize: 13,
              fontWeight: 500,
              color: BRAND.teal,
              cursor: 'pointer',
            }}
          >
            Forgot password?
          </button>
        </div>

        {/* Sign in button */}
        <button
          onClick={handleSubmit}
          disabled={!canSubmit}
          style={{
            width: '100%',
            height: 52,
            borderRadius: 12,
            border: 'none',
            backgroundColor: canSubmit ? BRAND.teal : T.border,
            fontFamily: BRAND.fontSans,
            fontSize: 14,
            fontWeight: 600,
            color: canSubmit ? T.white : T.textMuted,
            cursor: canSubmit ? 'pointer' : 'not-allowed',
            transition: 'all 200ms ease',
          }}
        >
          {isLoading ? 'Signing in...' : 'Sign in'}
        </button>

        {/* Footer */}
        <p
          style={{
            textAlign: 'center',
            fontFamily: BRAND.fontSans,
            fontSize: 13,
            color: BRAND.inkLight,
            marginTop: 16,
            marginBottom: 0,
          }}
        >
          Don't have an account?{' '}
          <button
            onClick={() => navigate('/signup')}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              fontFamily: BRAND.fontSans,
              color: BRAND.teal,
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Sign up &rarr;
          </button>
        </p>
      </div>
    </div>
  );
}

export default LoginEmailPage;
